import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Calendar, Phone, Mail, Users, MessageSquare, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SectionHeader } from "@/components/admin/common";

export default function AdminReservations() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reservations");
      setRows(data);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div>
      <SectionHeader
        title="Reservation requests"
        description="Incoming booking requests from the public Reservation form."
      />
      <div className="flex justify-end mb-4">
        <Button
          variant="outline"
          onClick={load}
          data-testid="admin-refresh-reservations"
          className="rounded-full h-10"
        >
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="grid place-items-center h-40">
          <Loader2 className="h-6 w-6 animate-spin text-[#E53935]" />
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#E8DED5] bg-white p-10 text-center text-[#5C534D]">
          No reservation requests yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {rows.map((r) => (
            <div
              key={r.id}
              data-testid={`admin-reservation-${r.id}`}
              className="rounded-3xl bg-white border border-[#E8DED5] p-5 md:p-6 space-y-3"
            >
              <div className="flex items-center justify-between">
                <div className="font-display font-bold text-lg">{r.name}</div>
                <span className="text-[11px] tracking-widest uppercase font-bold px-3 py-1 rounded-full bg-[#E53935]/10 text-[#E53935]">
                  {r.status || "pending"}
                </span>
              </div>
              <div className="space-y-2 text-sm">
                <Line icon={Calendar}>
                  {r.date} · {r.time}
                </Line>
                <Line icon={Users}>
                  {r.party_size} {r.party_size === 1 ? "guest" : "guests"}
                </Line>
                <Line icon={Phone}>{r.phone}</Line>
                <Line icon={Mail}>{r.email}</Line>
                {r.message && (
                  <Line icon={MessageSquare}>
                    <span className="text-[#5C534D]">{r.message}</span>
                  </Line>
                )}
              </div>
              <div className="pt-3 border-t border-[#E8DED5] text-xs text-[#5C534D]">
                Requested {new Date(r.created_at).toLocaleString()}
                {r.newsletter ? " · wants newsletter" : ""}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Line({ icon: Icon, children }) {
  return (
    <div className="flex items-start gap-2 text-[#1A1A1A]">
      <Icon className="h-4 w-4 mt-0.5 text-[#5C534D] shrink-0" />
      <div className="min-w-0 break-words">{children}</div>
    </div>
  );
}
